"use client";

import React, { useState } from "react";
import CustomModal from "@/components/modal/CustomModal";
import Icon from "@/components/icons/Icons";
import {
  handleField,
  validateInputData,
  ErrorMap,
  ValidationType,
} from "@/utils/allValidations";
import { apiPost } from "@/utils/apiClient";

interface AskQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AskQuestionModal({
  isOpen,
  onClose,
}: AskQuestionModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    qdescription: "",
  });

  const [errors, setErrors] = useState<ErrorMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetForm = () => {
    setFormData({ name: "", email: "", qdescription: "" });
    setErrors({});
    setIsLoading(false);
    setIsSuccess(false);
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (
    field: keyof typeof formData,
    type: ValidationType,
    val: string,
  ) => {
    // Enforce max character lengths
    let value = val;
    if (field === "name" && value.length > 100) {
      value = value.slice(0, 100);
    } else if (field === "email" && value.length > 150) {
      value = value.slice(0, 150);
    } else if (field === "qdescription" && value.length > 5000) {
      value = value.slice(0, 5000);
    }

    handleField({
      type,
      value,
      field,
      mode: "change",
      setValue: (v) => setFormData((prev) => ({ ...prev, [field]: v })),
      setErrors,
      prevValue: formData[field],
      allowSpace: field !== "email",
      validateOnChange: false,
    });

    if (submitError) {
      setSubmitError(null);
    }
  };

  const handleBlur = (field: keyof typeof formData, type: ValidationType) => {
    handleField({
      type,
      value: formData[field],
      field,
      mode: "blur",
      setErrors,
      prevValue: formData[field],
      allowSpace: field !== "email",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const isNameValid = validateInputData("name", formData.name);
    const isEmailValid = validateInputData("email", formData.email);
    const isDescValid = validateInputData("message", formData.qdescription);

    let hasError = false;
    const newErrors: ErrorMap = {};

    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name";
      hasError = true;
    } else if (!isNameValid) {
      newErrors.name = "Please enter a valid name";
      hasError = true;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address";
      hasError = true;
    } else if (!isEmailValid) {
      newErrors.email = "Please enter a valid email address";
      hasError = true;
    }

    if (!formData.qdescription.trim()) {
      newErrors.qdescription = "Please describe your question";
      hasError = true;
    } else if (!isDescValid || formData.qdescription.trim().length < 10) {
      newErrors.qdescription =
        "Question must be at least 10 characters for clarity";
      hasError = true;
    } else if (formData.qdescription.length > 5000) {
      newErrors.qdescription = "Question cannot exceed 5000 characters";
      hasError = true;
    }

    if (hasError) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        name: formData.name.trim().slice(0, 100),
        email: formData.email.trim().slice(0, 150),
        qdescription: formData.qdescription.trim().slice(0, 5000),
      };

      const res = await apiPost("data/askQuestionWeb", payload);

      if (res && res.status === 0) {
        setSubmitError(
          res.message || "Failed to submit question. Please try again.",
        );
      } else {
        setIsSuccess(true);
      }
    } catch (err) {
      console.error("Error submitting question:", err);
      setSubmitError("Failed to submit question. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={false}
      closeOnOverlayClick={!isLoading}
      isBlurOverlay
      maxSize="max-w-xl"
      className="w-full bg-white rounded-t-4xl! sm:rounded-6xl! p-4 sm:p-6 shadow-2xl relative border border-slate-200"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
            Ask a Question
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Get answers and insights from our mutual fund community and
            advisors.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={isLoading}
          aria-label="Close modal"
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition cursor-pointer disabled:opacity-50"
        >
          <Icon.crossIcon className="w-4 h-4" />
        </button>
      </div>

      {isSuccess ? (
        <div className="text-center py-6 sm:py-8 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center text-3xl font-bold">
            ✓
          </div>
          <div className="space-y-1.5">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900">
              Question Submitted Successfully!
            </h3>
            <p className="text-xs sm:text-sm sm:leading-5 text-slate-500 max-w-md mx-auto leading-relaxed">
              Thank you for asking. Your question is currently under review by
              our team and will appear in the Community Q&A once approved.
            </p>
          </div>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={resetForm}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-slate-300 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer"
            >
              Ask Another Question
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-primaryBlue hover:bg-primaryBlue/90 text-white text-xs sm:text-sm font-semibold transition cursor-pointer shadow-xs"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {submitError && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs sm:text-sm font-medium text-red-800">
              {submitError}
            </div>
          )}

          {/* Name Field */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="ask_modal_name"
                className="block text-xs font-semibold text-slate-600"
              >
                Your Name <span className="text-red-500">*</span>
              </label>
              <span className="text-2xs text-slate-400">
                {formData.name.length}/100
              </span>
            </div>
            <input
              id="ask_modal_name"
              type="text"
              maxLength={100}
              value={formData.name}
              onChange={(e) => handleChange("name", "name", e.target.value)}
              onBlur={() => handleBlur("name", "name")}
              placeholder="e.g. Rahul Sharma"
              className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm text-slate-900 placeholder-slate-400 outline-hidden transition bg-white ${
                errors.name
                  ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  : "border-slate-300 focus:border-primaryBlue focus:ring-2 focus:ring-primaryBlue/20"
              }`}
            />
            {errors.name && (
              <p className="text-2xs text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="ask_modal_email"
                className="block text-xs font-semibold text-slate-600"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <span className="text-2xs text-slate-400">
                {formData.email.length}/150
              </span>
            </div>
            <input
              id="ask_modal_email"
              type="email"
              maxLength={150}
              value={formData.email}
              onChange={(e) => handleChange("email", "email", e.target.value)}
              onBlur={() => handleBlur("email", "email")}
              placeholder="e.g. rahul@example.com"
              className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm text-slate-900 placeholder-slate-400 outline-hidden transition bg-white ${
                errors.email
                  ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  : "border-slate-300 focus:border-primaryBlue focus:ring-2 focus:ring-primaryBlue/20"
              }`}
            />
            {errors.email && (
              <p className="text-2xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Question Description Field */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="ask_modal_qdescription"
                className="block text-xs font-semibold text-slate-600"
              >
                Question Details <span className="text-red-500">*</span>
              </label>
              <span
                className={`text-2xs ${
                  formData.qdescription.length >= 5000
                    ? "text-red-500 font-semibold"
                    : "text-slate-400"
                }`}
              >
                {formData.qdescription.length}/5000
              </span>
            </div>
            <textarea
              id="ask_modal_qdescription"
              rows={5}
              maxLength={5000}
              value={formData.qdescription}
              onChange={(e) =>
                handleChange("qdescription", "message", e.target.value)
              }
              onBlur={() => handleBlur("qdescription", "message")}
              placeholder="Provide context or details about your question... (e.g. Which index fund is suitable for a 10-year investment horizon?)"
              className={`w-full p-4 rounded-xl border text-xs sm:text-sm sm:leading-5 text-slate-900 placeholder-slate-400 outline-hidden transition bg-white resize-y min-h-30 max-h-70 leading-relaxed ${
                errors.qdescription
                  ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  : "border-slate-300 focus:border-primaryBlue focus:ring-2 focus:ring-primaryBlue/20"
              }`}
            />
            {errors.qdescription && (
              <p className="text-2xs text-red-500 mt-1">
                {errors.qdescription}
              </p>
            )}
            <p className="text-2xs text-slate-400 mt-1.5">
              Maximum 5000 characters. Keep it clear and specific to receive
              helpful answers.
            </p>
          </div>

          {/* Actions */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-5 py-2.5 rounded-xl border border-slate-300 text-xs sm:text-sm font-semibold text-slate-600 hover:bg-slate-50 transition cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 rounded-xl bg-primaryBlue hover:bg-primaryBlue/90 text-white text-xs sm:text-sm font-semibold transition cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Question"
              )}
            </button>
          </div>
        </form>
      )}
    </CustomModal>
  );
}
